import { Search } from 'lucide-react';
import { Department, Category, ThemeColors } from '../../types';

interface MobileMenuProps {
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  departments: Department[];
  categories: Category[];
  selectedDepartmentId: number | null;
  selectedCategoryId: number | null;
  onDepartmentClick: (departmentId: number | null) => void;
  onCategoryClick: (categoryId: number) => void;
  onAllProductsClick: () => void;
  themeColors?: ThemeColors;
}

export function MobileMenu({
  searchQuery,
  onSearchChange,
  departments,
  categories,
  selectedDepartmentId,
  selectedCategoryId,
  onDepartmentClick,
  onCategoryClick,
  onAllProductsClick,
  themeColors
}: MobileMenuProps) {
  const isActiveDepartment = (departmentId: number | null) => {
    if (departmentId === null) {
      return selectedDepartmentId === null;
    }
    return Number(selectedDepartmentId) === Number(departmentId);
  };

  const isActiveCategory = (categoryId: number) => {
    return selectedCategoryId === categoryId;
  };

  const isAllProductsActive = () => {
    return !selectedCategoryId;
  };

  return (
    <div id="mobile-menu" className="md:hidden py-4 border-t border-gray-700">
      <div className="flex flex-col space-y-4">
        <div className="relative">
          <label htmlFor="mobile-search" className="sr-only">Search products</label>
          <input
            id="mobile-search"
            type="search"
            placeholder="Search products..."
            value={searchQuery}
            onChange={onSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            aria-label="Search products"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" aria-hidden="true" />
        </div>

        {/* Departments */}
        <div>
          <h3
            className="text-sm font-medium mb-2 px-4"
            style={{ color: themeColors?.left_side_heading_color || '#9ca3af' }}
          >
            Departments
          </h3>
          <nav className="flex flex-col space-y-1" aria-label="Mobile department navigation">
            <button
              onClick={() => onDepartmentClick(null)}
              className={`text-left py-2 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isActiveDepartment(null)
                  ? 'bg-blue-600 font-medium'
                  : 'hover:bg-gray-700'
              }`}
              style={{
                color: isActiveDepartment(null)
                  ? themeColors?.left_side_active_color || '#ffffff'
                  : themeColors?.left_side_color || '#e5e7eb'
              }}
              aria-current={isActiveDepartment(null) ? 'page' : undefined}
            >
              All Departments
            </button>
            {departments.map(dept => (
              <button
                key={dept.id}
                onClick={() => onDepartmentClick(dept.id)}
                className={`text-left py-2 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  isActiveDepartment(dept.id)
                    ? 'bg-blue-600 font-medium'
                    : 'hover:bg-gray-700'
                }`}
                style={{
                  color: isActiveDepartment(dept.id)
                    ? themeColors?.left_side_active_color || '#ffffff'
                    : themeColors?.left_side_color || '#e5e7eb'
                }}
                aria-current={isActiveDepartment(dept.id) ? 'page' : undefined}
              >
                {dept.name} {dept.memo && `(${dept.memo})`}
              </button>
            ))}
          </nav>
        </div>

        {/* Categories */}
        <div>
          <h3
            className="text-sm font-medium mb-2 px-4"
            style={{ color: themeColors?.left_side_heading_color || '#9ca3af' }}
          >
            Categories
          </h3>
          <nav className="flex flex-col space-y-1" aria-label="Mobile category navigation">
            <button
              onClick={onAllProductsClick}
              className={`text-left py-2 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isAllProductsActive()
                  ? 'bg-blue-600 font-medium'
                  : 'hover:bg-gray-700'
              }`}
              style={{
                color: isAllProductsActive()
                  ? themeColors?.left_side_active_color || '#ffffff'
                  : themeColors?.left_side_color || '#e5e7eb'
              }}
              aria-current={isAllProductsActive() ? 'page' : undefined}
            >
              All Products
            </button>
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => onCategoryClick(category.id)}
                className={`text-left py-2 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  isActiveCategory(category.id)
                    ? 'bg-blue-600 font-medium'
                    : 'hover:bg-gray-700'
                }`}
                style={{
                  color: isActiveCategory(category.id)
                    ? themeColors?.left_side_active_color || '#ffffff'
                    : themeColors?.left_side_color || '#e5e7eb'
                }}
                aria-current={isActiveCategory(category.id) ? 'page' : undefined}
              >
                {category.name} {category.memo && `(${category.memo})`}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}
